import React, { FC, useCallback, useState } from 'react';

import Maybe from 'graphql/tsutils/Maybe';
import { useIntl } from 'react-intl';

import { RewardTierSelectionModal } from 'components/app-header/loyalty/rewards-management/reward-tier-selection-modal';
import { useSetLoyaltyCardRewardsTierMutation } from 'generated/rbi-graphql';
import { useSelectedRewardTier } from 'hooks/loyalty/use-selected-reward';
import useErrorModal from 'hooks/use-error-modal';
import { ILoyaltyCategoryOption } from 'state/loyalty/types';

import { RewardTierSkeleton } from './reward-tier.skeleton';
import {
  Content,
  ContentWrapper,
  LinkButton,
  PointsLabel,
  Title,
  Wrapper,
} from './reward-tier.styled';

export const RewardTier: FC<{
  selectedTier: Maybe<Omit<ILoyaltyCategoryOption, 'description'>>;
}> = ({ selectedTier, ...props }) => {
  const [showRewardSelectionModal, setShowhowRewardSelectionModal] = useState(false);
  const { formatMessage } = useIntl();

  const openRewardsModal = useCallback(() => setShowhowRewardSelectionModal(true), [
    setShowhowRewardSelectionModal,
  ]);
  const closeRewardsModal = useCallback(() => setShowhowRewardSelectionModal(false), [
    setShowhowRewardSelectionModal,
  ]);

  const [setSelectedRewardsTier] = useSetLoyaltyCardRewardsTierMutation();

  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Setting Reward Tier Failure',
  });

  const onSetRewardsTierError = useCallback(
    () => openErrorDialog({ message: formatMessage({ id: 'setRewardsTierError' }) }),
    [formatMessage, openErrorDialog]
  );

  const {
    onChangeRewardTier,
    selectedRewardTierId,
    selectedRewardCategoryId,
  } = useSelectedRewardTier(null, setSelectedRewardsTier, { onSetRewardsTierError });

  if (!selectedTier || selectedRewardTierId !== selectedTier._id) {
    return <RewardTierSkeleton {...props} />;
  }

  return (
    <Wrapper {...props}>
      <Title>{formatMessage({ id: 'rewardsManagementChoosenLevel' })}</Title>
      <ContentWrapper>
        <Content data-testid="reward-tier-title">{selectedTier.name.locale}</Content>
        <PointsLabel data-testid="reward-tier-points">
          {formatMessage({ id: 'rewardsManagementPoints' }, { points: selectedTier.points })}
        </PointsLabel>
      </ContentWrapper>
      <LinkButton onClick={openRewardsModal}>
        {formatMessage({ id: 'rewardsManagementChangeRewardLevel' })}
      </LinkButton>
      {showRewardSelectionModal && (
        <RewardTierSelectionModal
          onDismiss={closeRewardsModal}
          radioButtonName="change-reward-level-modal-reward-tier"
          onChangeRewardTier={onChangeRewardTier}
          selectedRewardTierId={selectedRewardTierId}
          selectedRewardCategoryId={selectedRewardCategoryId}
        />
      )}
      <ErrorDialog />
    </Wrapper>
  );
};
